import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type Delivery,
  type ListRouteParams,
} from "../../domain/entities/delivery";
import { type DeliveryDetail } from "../../domain/entities/delivery-detail";
import {
  ConfirmDeliveryError,
  GetDeliveryDetailError,
  ListRouteError,
  StartRouteError,
} from "../../domain/errors/errors";
import {
  type ConfirmDeliveryParams,
  type DeliveryRepository,
  type StartRouteParams,
} from "../../domain/repos/delivery-repo";
import { type DetailResponse } from "../../infrastructure/responses/detail-response";
import { type ListRouteResponse } from "../../infrastructure/responses/list-route-response";
import { type ParameterResponse } from "../../infrastructure/responses/parameter-response";

/** Business-parameter key for the customer's card-activation link. */
const ACTIVATION_URL_KEY = "origination_form_url";

export class DeliveryRepo implements DeliveryRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listRoute(params: ListRouteParams): Promise<Delivery[]> {
    try {
      // The get() helper takes a URL only, so build the (snake_case) query here.
      const query = new URLSearchParams({
        assigned_ambassador_id: params.assignedAmbassadorId,
      });
      if (params.deliverySlotFrom) {
        query.set("delivery_slot_from", params.deliverySlotFrom);
      }
      if (params.deliverySlotTo) {
        query.set("delivery_slot_to", params.deliverySlotTo);
      }

      const response = await this.apiClient.get<ListRouteResponse>(
        `v1/requests?${query.toString()}`,
      );
      const items = response.data?.items ?? [];
      const slotTime = (d: Delivery): number => {
        const t = d.deliverySlotAt ? new Date(d.deliverySlotAt).getTime() : NaN;
        return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
      };
      return [...items].sort((a, b) => slotTime(a) - slotTime(b));
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListRouteError(message, apiError);
    }
  }

  async getDetail(requestId: string): Promise<DeliveryDetail> {
    try {
      const response = await this.apiClient.get<DetailResponse>(
        `v1/requests/${requestId}`,
      );
      if (!response.data) {
        throw new GetDeliveryDetailError("Delivery not found");
      }
      return response.data;
    } catch (error) {
      if (error instanceof GetDeliveryDetailError) {
        throw error;
      }
      const { message, apiError } = extractApiError(error);
      throw new GetDeliveryDetailError(message, apiError);
    }
  }

  async startRoute(params: StartRouteParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/start`, {
        lat: params.lat,
        long: params.long,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new StartRouteError(message, apiError);
    }
  }

  async confirmDelivery(params: ConfirmDeliveryParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/delivery`, {
        lat: params.lat,
        long: params.long,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ConfirmDeliveryError(message, apiError);
    }
  }

  async getActivationUrl(): Promise<string> {
    // The activation link is cosmetic to the flow — never block the POD screen
    // on it; an empty string just means the WhatsApp message goes without a link.
    try {
      const response = await this.apiClient.get<ParameterResponse>(
        `v1/parameters/${ACTIVATION_URL_KEY}`,
      );
      return response.data?.valueText ?? "";
    } catch {
      return "";
    }
  }
}
